import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { initMetaPixel, revokeMetaPixel } from '../utils/metaPixel';
export type CookieConsent = {
  marketing: boolean;
  decidedAt: string;
  version: number;
};
type CookieConsentContextValue = {
  consent: CookieConsent | null;
  isSettingsOpen: boolean;
  isBannerVisible: boolean;
  acceptAll: () => void;
  rejectAll: () => void;
  savePreferences: (marketing: boolean) => void;
  openSettings: () => void;
  closeSettings: () => void;
};
const STORAGE_KEY = 'naturia-cookie-consent';
const CONSENT_VERSION = 1;
const CookieConsentContext = createContext<CookieConsentContextValue | null>(null);
export function CookieConsentProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [consent, setConsent] = useState<CookieConsent | null>(readStoredConsent);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  useEffect(() => {
    if (consent?.marketing) {
      initMetaPixel();
    } else if (consent) {
      revokeMetaPixel();
    }
  }, [consent]);
  const persist = useCallback((marketing: boolean) => {
    const next: CookieConsent = {
      marketing,
      decidedAt: new Date().toISOString(),
      version: CONSENT_VERSION
    };
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch {
      // El almacenamiento puede estar bloqueado; la elección se aplica igualmente en esta sesión.
    }
    setConsent(next);
    setIsSettingsOpen(false);
  }, []);
  const value = useMemo<CookieConsentContextValue>(() => ({
    consent,
    isSettingsOpen,
    isBannerVisible: consent === null || isSettingsOpen,
    acceptAll: () => persist(true),
    rejectAll: () => persist(false),
    savePreferences: persist,
    openSettings: () => setIsSettingsOpen(true),
    closeSettings: () => setIsSettingsOpen(false)
  }), [consent, isSettingsOpen, persist]);
  return <CookieConsentContext.Provider value={value} data-id="element-240" data-mp="251ncrn9ufr2r">{children}</CookieConsentContext.Provider>;
}
export function useCookieConsent(): CookieConsentContextValue {
  const context = useContext(CookieConsentContext);
  if (!context) {
    throw new Error('useCookieConsent debe usarse dentro de CookieConsentProvider');
  }
  return context;
}
function readStoredConsent(): CookieConsent | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<CookieConsent>;
    if (parsed.version !== CONSENT_VERSION || typeof parsed.marketing !== 'boolean') return null;
    return parsed as CookieConsent;
  } catch {
    return null;
  }
}